/**
 * Complete Look Dynamic Update System
 * Handles dynamic updates of "Complete o visual" products based on selected color variant
 */

class CompleteLook {
  constructor() {
    this.section = document.querySelector('.complete-look-section');
    if (!this.section) {
      return;
    }

    // Find container - could be regular grid or carousel
    this.container = this.section.querySelector('.complete-look-products, .complete-look-products--carousel');
    if (!this.container) {
      return;
    }

    this.productId = this.section.dataset.productId;
    this.completeLookData = this.parseCompleteLookData();
    this.currentColor = this.container.dataset.selectedColor;
    this.productsCache = new Map();
    this.lazyLoadObserver = null;
    this.currentBatchIndex = 0;

    // "Estilo do card" = Blazee: os cards vêm prontos da view product.blz-card (o mesmo card da coleção).
    // Com "Atual" nada disso é usado e o card continua montado à mão (createProductCard).
    this.isBlazee = this.section.dataset.cardStyle === 'blazee';
    this.blzCardCache = new Map();
    this.blzRenderToken = 0;

    this.init();
  }

  async init() {
    // Setup listeners
    this.listenForVariantChanges();
    this.setupProductRerender();

    // Initialize with current variant (lazy-loading enabled)
    await this.initializeWithCurrentVariant();

    // Initialize related kits functionality
    await this.initializeRelatedKits();

    // Setup periodic check for related kits (every 1 second like original)
    setInterval(() => {
      this.initializeRelatedKits();
    }, 1000);
  }
  
  parseCompleteLookData() {
    try {
      const data = JSON.parse(this.container.dataset.completeLookData || '{}');
      return data;
    } catch (e) {
      return {};
    }
  }
  
  async fetchAndCacheProduct(handle) {
    // Check if already cached
    if (this.productsCache.has(handle)) {
      return this.productsCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const product = await response.json();
      this.productsCache.set(handle, product);
      return product;
    } catch (error) {
      return null;
    }
  }

  /**
   * Load products in batches to improve performance
   */
  async loadProductsBatch(productsData, startIndex, batchSize = 6) {
    const batch = productsData.slice(startIndex, startIndex + batchSize);
    const promises = batch.map(productData =>
      this.fetchAndCacheProduct(productData.handle)
    );
    await Promise.allSettled(promises);
    return batch;
  }

  /**
   * Render skeleton placeholder cards
   */
  renderSkeletonCards(count) {
    const skeletons = Array(count).fill('').map(() => `
      <div class="complete-look-product-item skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-title"></div>
        <div class="skeleton-price"></div>
      </div>
    `).join('');

    this.container.innerHTML = skeletons;
  }

  /**
   * Load a batch of products and replace skeleton cards with real content
   */
  async loadAndRenderBatch(productsData, startIndex, batchSize) {
    const batch = await this.loadProductsBatch(productsData, startIndex, batchSize);

    batch.forEach((productData, i) => {
      const index = startIndex + i;
      const skeletonCard = this.container.querySelector(
        `.complete-look-product-item:nth-child(${index + 1})`
      );

      if (!skeletonCard) return;

      const product = this.productsCache.get(productData.handle);
      if (product) {
        const cardHTML = this.createProductCard(product, productData.variant_id);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cardHTML;
        const newCard = tempDiv.firstElementChild;

        if (newCard) {
          skeletonCard.replaceWith(newCard);
        }
      } else {
        // If product failed to load, remove skeleton
        skeletonCard.remove();
      }
    });
  }

  /**
   * Setup Intersection Observer for lazy loading additional products
   */
  setupLazyLoading(productsData, loadedCount) {
    if (loadedCount >= productsData.length) {
      return; // All products already loaded
    }

    // Disconnect previous observer if exists
    if (this.lazyLoadObserver) {
      this.lazyLoadObserver.disconnect();
    }

    // Find the sentinel card (2 cards before the last loaded one)
    const sentinelIndex = Math.max(0, loadedCount - 2);
    const sentinelCard = this.container.querySelector(
      `.complete-look-product-item:nth-child(${sentinelIndex + 1})`
    );

    if (!sentinelCard) {
      return;
    }

    // Create observer that loads next batch when sentinel is visible
    this.lazyLoadObserver = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && this.currentBatchIndex < productsData.length) {
          // Disconnect observer temporarily while loading
          this.lazyLoadObserver.disconnect();

          // Load next batch
          const batchSize = 6;
          await this.loadAndRenderBatch(productsData, this.currentBatchIndex, batchSize);
          this.currentBatchIndex += batchSize;

          // Setup observer for next batch
          this.setupLazyLoading(productsData, this.currentBatchIndex);
        }
      },
      {
        root: this.container,
        rootMargin: '200px', // Start loading 200px before sentinel is visible
        threshold: 0.1
      }
    );

    this.lazyLoadObserver.observe(sentinelCard);
  }

  async initializeWithCurrentVariant() {
    // Get the currently selected variant from the form
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) {
      return;
    }

    try {
      // Fetch product data to get the variant details
      const product = await this.fetchProductData();

      const variant = product.variants.find(v => v.id == variantInput.value);

      if (variant && variant.option1) {
        // Initialize complete look with the current color
        await this.updateCompleteLook(variant.option1);
      }
    } catch (error) {
      // Silent error
    }
  }
  
  listenForVariantChanges() {
    // Listen for custom variant:change event
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant && event.detail.variant.option1) {
        this.updateCompleteLook(event.detail.variant.option1);
      }
    });

    // Also listen for native change events on variant selectors
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('change', (event) => {
        this.handleVariantPickerChange();
      });
    }
  }
  
  setupProductRerender() {
    // Listen for product-rerender events (used by some themes)
    const productRerender = document.querySelector('product-rerender');
    if (productRerender) {
      productRerender.addEventListener('rerender', () => {
        this.handleProductRerender();
      });
    }
  }
  
  async handleVariantPickerChange() {
    // Add a small delay to allow the form to update
    setTimeout(async () => {
      // Get the currently selected variant
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) {
        return;
      }

      const variantId = form.querySelector('input[name="id"]')?.value;
      if (!variantId) {
        return;
      }

      try {
        const product = await this.fetchProductData();
        const variant = product.variants.find(v => v.id == variantId);

        if (variant && variant.option1) {
          // Only update if color actually changed
          if (variant.option1 !== this.currentColor) {
            this.updateCompleteLook(variant.option1);
          }
        }
      } catch (error) {
        // Silent error
      }
    }, 100); // 100ms delay to allow form update
  }
  
  async handleProductRerender() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return;

    try {
      const product = await this.fetchProductData();
      const variant = product.variants.find(v => v.id == variantInput.value);

      if (variant && variant.option1) {
        this.updateCompleteLook(variant.option1);
      }
    } catch (error) {
      // Silent error
    }
  }
  
  async fetchProductData() {
    const handle = window.location.pathname.split('/').pop();
    const response = await fetch(`/products/${handle}.js`);
    const data = await response.json();
    return data;
  }
  
  async updateCompleteLook(selectedColor) {
    // Skip if color hasn't actually changed
    if (selectedColor === this.currentColor && this.section.style.display !== 'none') {
      return;
    }

    // Disconnect any existing lazy load observer
    if (this.lazyLoadObserver) {
      this.lazyLoadObserver.disconnect();
      this.lazyLoadObserver = null;
    }

    // Reset batch index
    this.currentBatchIndex = 0;

    // Blazee: invalida um carregamento anterior ainda em andamento (troca rápida de cor)
    if (this.isBlazee) {
      this.blzRenderToken++;
    }

    // Check if we have data for this color
    const colorData = this.completeLookData[selectedColor];

    if (!colorData || !colorData.products || colorData.products.length === 0) {
      this.currentColor = selectedColor; // Update color even when hiding
      this.hideSection();
      // Still initialize related kits even when complete look is hidden
      await this.initializeRelatedKits();
      return;
    }

    // Update current color
    this.currentColor = selectedColor;

    // Update title and description
    this.updateTitleAndDescription(colorData);

    // Find the correct container (could be scroll-carousel or regular div)
    const targetContainer = this.section.querySelector('.complete-look-products, .complete-look-products--carousel');
    if (targetContainer) {
      targetContainer.dataset.selectedColor = selectedColor;
      this.container = targetContainer;
    }

    // Blazee: o Liquid já renderizou os cards desta cor na carga da página; só mostra o bloco.
    if (this.isBlazee && this.container.dataset.renderedColor === selectedColor &&
        this.container.querySelector('.complete-look-product-item blz-card')) {
      this.showSection();
      await this.initializeRelatedKits();
      return;
    }

    // Show section and render products with lazy-loading
    this.showSection();
    await this.renderProducts(colorData.products);

    // Also update related kits when complete look updates
    await this.initializeRelatedKits();
  }
  
  updateTitleAndDescription(colorData) {
    const titleElement = this.section.querySelector('.complete-look-title');
    const descriptionElement = this.section.querySelector('.complete-look-description');

    if (titleElement) {
      if (colorData.title) {
        titleElement.textContent = colorData.title;
        titleElement.style.display = 'block';
      } else {
        // Use default title from block settings if available
        const blockSettings = this.getBlockSettings();
        if (blockSettings && blockSettings.title) {
          titleElement.textContent = blockSettings.title;
          titleElement.style.display = 'block';
        } else {
          titleElement.textContent = 'Complete o visual';
          titleElement.style.display = 'block';
        }
      }
    }

    if (descriptionElement) {
      if (colorData.description) {
        descriptionElement.textContent = colorData.description;
        descriptionElement.style.display = 'block';
      } else {
        descriptionElement.style.display = 'none';
      }
    }
  }
  
  getBlockSettings() {
    try {
      const settingsData = this.container.dataset.blockSettings;
      if (settingsData) {
        return JSON.parse(settingsData);
      }
    } catch (e) {
      // Silent error
    }
    return null;
  }
  
  async renderProducts(productsData) {
    if (this.isBlazee) {
      return this.renderBlazeeProducts(productsData);
    }

    // Show loading state
    this.container.classList.add('loading');

    try {
      // Get max products from settings or default to 20
      const blockSettings = this.getBlockSettings();
      const maxProducts = blockSettings?.products_count || 20;
      const totalProducts = Math.min(productsData.length, maxProducts);

      if (totalProducts === 0) {
        this.container.innerHTML = '<div class="complete-look-empty">Nenhum produto disponível</div>';
        return;
      }

      // Initial batch size (load immediately)
      const initialBatchSize = 8;

      // 1. Render skeleton cards for all products
      this.renderSkeletonCards(totalProducts);

      // 2. Load and render first batch immediately
      const firstBatchSize = Math.min(initialBatchSize, totalProducts);
      await this.loadAndRenderBatch(productsData, 0, firstBatchSize);

      // 3. Reset batch index for lazy loading
      this.currentBatchIndex = firstBatchSize;

      // 4. Setup lazy loading for remaining products (if any)
      if (totalProducts > firstBatchSize) {
        this.setupLazyLoading(productsData, firstBatchSize);
      }

      // If it's a carousel, reinitialize it
      if (this.container.classList.contains('complete-look-products--carousel')) {
        this.reinitializeCarousel();
      }
    } catch (error) {
      this.container.innerHTML = '<div class="complete-look-error">Erro ao carregar produtos</div>';
    } finally {
      this.container.classList.remove('loading');
    }
  }
  
  /**
   * Blazee: busca o card de cada entrada na view /products/<handle>?variant=<id>&view=blz-card, todas em paralelo,
   * e troca os cards de uma vez, na ordem da lista. Card repetido entra uma vez; entrada que falhar fica de fora;
   * no máximo products_count cards (como o Liquid). Os <blz-card> se ativam sozinhos ao entrar no documento.
   */
  async renderBlazeeProducts(productsData) {
    const token = this.blzRenderToken;
    const blockSettings = this.getBlockSettings() || {};
    const maxProducts = Number(blockSettings.products_count) || 4;

    const seen = {};
    const list = [];
    for (let i = 0; i < productsData.length && list.length < 20; i++) {
      const data = productsData[i];
      if (!data || !data.handle) continue;
      const variantId = this.cleanVariantId(data.variant_id);
      if (variantId) {
        if (seen[variantId]) continue;
        seen[variantId] = true;
      }
      list.push(data);
    }

    this.container.classList.add('loading');
    const htmls = await Promise.all(list.map((data) => this.fetchBlzCard(data)));

    // outra cor foi escolhida enquanto carregava: esta resposta não vale mais
    if (token !== this.blzRenderToken) return;
    this.container.classList.remove('loading');

    // mesmo card duas vezes (mesmo produto e mesma foto, ex.: o mesmo loafer em 39 e 41) entra uma vez, como no Liquid
    const template = document.createElement('template');
    template.innerHTML = htmls.filter(Boolean).join('');
    const keys = {};
    const items = Array.prototype.filter.call(template.content.children, (el) => {
      if (!el.classList.contains('complete-look-product-item')) return false;
      const key = el.getAttribute('data-card-key');
      if (key) {
        if (keys[key]) return false;
        keys[key] = true;
      }
      return true;
    }).slice(0, maxProducts);

    if (!items.length) {
      this.container.innerHTML = '';
      delete this.container.dataset.renderedColor;
      this.hideSection();
      return;
    }

    // a view não conhece o bloco: aplica aqui o que o Liquid do bloco aplicaria
    items.forEach((item) => {
      if (blockSettings.hide_product_information === true) {
        const info = item.querySelector('.blz-card__info');
        if (info) info.parentNode.removeChild(info);
      }
      if (blockSettings.show_secondary_image === false) {
        const card = item.querySelector('blz-card');
        const extra = item.querySelectorAll('.blz-card__slide + .blz-card__slide, .blz-card__dots');
        Array.prototype.forEach.call(extra, (el) => el.parentNode.removeChild(el));
        if (card) card.classList.remove('blz-card--multi');
      }
    });

    this.container.innerHTML = '';
    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(item));
    this.container.appendChild(fragment);
    this.container.dataset.renderedColor = this.currentColor;
    this.container.scrollLeft = 0;
  }

  cleanVariantId(variantId) {
    return String(variantId || '').replace('gid://shopify/ProductVariant/', '').trim();
  }

  fetchBlzCard(data) {
    const variantId = this.cleanVariantId(data.variant_id);
    const key = data.handle + '|' + variantId;
    if (this.blzCardCache.has(key)) {
      return this.blzCardCache.get(key);
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let url = root + 'products/' + encodeURIComponent(data.handle) + '?view=blz-card';
    if (variantId) {
      url += '&variant=' + encodeURIComponent(variantId);
    }

    const request = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        // só a célula do card; se a view não existir a loja devolve a página inteira, que não serve
        const card = html.trim();
        if (card.indexOf('<div class="complete-look-product-item') !== 0) throw new Error('sem card');
        return card;
      })
      .catch(() => {
        this.blzCardCache.delete(key);
        return null;
      });

    this.blzCardCache.set(key, request);
    return request;
  }

  getCurrentVariantId() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return '';
    
    const variantInput = form.querySelector('input[name="id"]');
    return variantInput ? variantInput.value : '';
  }
  
  reinitializeCarousel() {
    // Dispatch a custom event to notify the carousel that content has changed
    if (this.container.id) {
      const carouselElement = document.getElementById(this.container.id);
      if (carouselElement) {
        // Reset scroll position
        carouselElement.scrollLeft = 0;
        
        // Dispatch event for carousel to recalculate
        carouselElement.dispatchEvent(new CustomEvent('carousel:reinitialize'));
        
        // Update prev/next button states
        const prevButton = document.querySelector(`[aria-controls="${this.container.id}"] button`);
        const nextButton = document.querySelector(`carousel-next-button[aria-controls="${this.container.id}"] button`);
        
        if (prevButton) prevButton.disabled = true;
        if (nextButton) nextButton.disabled = false;
      }
    }
  }
  
  
  createProductCard(product, variantId) {
    // Find the specific variant if ID provided
    let variant = product.variants[0]; // default to first variant

    if (variantId) {
      // variantId comes as simple numeric ID, not gid format
      const foundVariant = product.variants.find(v => v.id == variantId);
      if (foundVariant) {
        variant = foundVariant;
      }
    }

    const price = (variant.price / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });

    const comparePrice = variant.compare_at_price > variant.price ?
      (variant.compare_at_price / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }) : null;

    // Extract color from variant title (usually the first option)
    const variantColor = variant.title.split(' / ')[0] || variant.title;

    // Calculate installment price (6x)
    const installmentPrice = (variant.price / 100 / 6).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });

    // Get the variant-specific image or fallback to product image
    const variantImage = variant.featured_media?.preview_image?.src ||
                        variant.image ||
                        product.featured_image;

    // Determine the URL based on variant availability
    const productUrl = variant.available
      ? `/products/${product.handle}?variant=${variant.id}`
      : `/products/${product.handle}`;

    // Create product card HTML matching the theme's structure
    return `
      <div class="complete-look-product-item scroll-snap-align-start" data-product-handle="${product.handle}">
        <article class="product-card">
          <a href="${productUrl}" class="product-card__figure rounded" data-instant>
            <div class="media media--ratio">
              <img src="${variantImage}"
                   alt="${product.title} - ${variant.title}"
                   loading="lazy"
                   fetchpriority="low"
                   class="product-card__image">
            </div>
          </a>
          <div class="product-card__info empty:hidden">
            <div class="v-stack justify-items-center gap-2">
              <div class="v-stack justify-items-center gap-1">
                <a href="${productUrl}" class="product-title h6" data-instant>
                  ${product.title}
                </a>
                <span class="text-subdued smallcaps">${variantColor}</span>
                <price-list class="price-list">
                  <sale-price class="h6 ${comparePrice ? 'text-on-sale' : 'text-subdued'}">
                    <span class="sr-only">Preço promocional</span>${price}
                  </sale-price>
                  ${comparePrice ? `
                    <compare-at-price class="h6 text-subdued line-through">
                      <span class="sr-only">Preço normal</span>${comparePrice}
                    </compare-at-price>
                  ` : ''}
                </price-list>
                <span class="linha_parcelamento_card" style="font-weight: 400; color: #898989; font-size: 12px;"><p style="margin: 0; text-align: center;">ou 6X <span class="money">${installmentPrice}</span> sem juros</p></span>
              </div>
            </div>
          </div>
        </article>
      </div>
    `;
  }
  
  showSection() {
    this.section.style.display = 'block';
    this.section.classList.remove('hidden');

    // Ensure container is visible
    const container = this.section.querySelector('.complete-look-container');
    if (container) {
      container.style.display = 'block';
    }

    // Ensure products wrapper is visible
    const wrapper = this.section.querySelector('.complete-look-products-wrapper');
    if (wrapper) {
      wrapper.style.display = 'block';
    }
  }
  
  hideSection() {
    this.section.style.display = 'none';
    this.section.classList.add('hidden');

    // Also hide title and description when hiding section
    const titleElement = this.section.querySelector('.complete-look-title');
    const descriptionElement = this.section.querySelector('.complete-look-description');

    if (titleElement) {
      titleElement.style.display = 'none';
    }

    if (descriptionElement) {
      descriptionElement.style.display = 'none';
    }
  }
  
  /**
   * Initialize related kits functionality
   */
  async initializeRelatedKits() {
    // Check if kits already exist (avoid duplicate rendering)
    if (document.getElementById('kits-relacionados')) {
      return;
    }

    // Find the div.aviso element to insert kits after it (same as the other script)
    const avisoElement = document.querySelector('div.aviso');
    if (!avisoElement) {
      return;
    }

    // Get kits data from the DOM (passed via Liquid)
    const kitsDataElement = document.getElementById('kits-relacionados-data');
    if (!kitsDataElement) {
      return;
    }

    let kitsMetafield;
    try {
      kitsMetafield = JSON.parse(kitsDataElement.textContent);
    } catch (error) {
      return;
    }

    // Handle nested array structure (sometimes the data comes as [[products]])
    if (Array.isArray(kitsMetafield) && kitsMetafield.length === 1 && Array.isArray(kitsMetafield[0])) {
      kitsMetafield = kitsMetafield[0];
    }

    if (!kitsMetafield || !Array.isArray(kitsMetafield) || kitsMetafield.length === 0) {
      return;
    }

    // Validate that we have product objects
    const validKitProducts = kitsMetafield.filter(kit => {
      if (kit && typeof kit === 'object' && kit.id && kit.title) {
        return true;
      }
      return false;
    });

    if (validKitProducts.length === 0) {
      return;
    }

    // Render the kits after the aviso element
    this.renderRelatedKits(validKitProducts, avisoElement);
  }
  
  /**
   * Render related kits buttons after aviso element
   */
  renderRelatedKits(kitProducts, avisoElement) {
    // Sort products: non-kits first, then kits by number
    const sortedProducts = [...kitProducts].sort((a, b) => {
      const aIsKit = a.title.toLowerCase().startsWith('kit');
      const bIsKit = b.title.toLowerCase().startsWith('kit');

      // Non-kits come first
      if (aIsKit !== bIsKit) {
        return aIsKit ? 1 : -1;
      }

      // If both are kits, sort by number
      if (aIsKit && bIsKit) {
        const aMatch = a.title.match(/kit\s+(\d+)/i);
        const bMatch = b.title.match(/kit\s+(\d+)/i);
        const aNumber = aMatch ? parseInt(aMatch[1]) : 0;
        const bNumber = bMatch ? parseInt(bMatch[1]) : 0;
        return aNumber - bNumber;
      }

      return 0;
    });

    // Create HTML structure matching the original exactly
    let html = `
      <div id="kits-relacionados" style="margin-top: 20px;">
        <h2>Ou se preferir</h2>
    `;

    let renderedCount = 0;

    // Add each kit button
    for (const kit of sortedProducts) {
      // First priority: try to get from metafield (like original code)
      const quantityMetafield = kit.metafields?.custom?.quantidade_de_itens_no_kit;
      let quantity = quantityMetafield?.value || quantityMetafield || null;

      // Second priority: if no metafield and title starts with Kit/Kits, extract number
      if (!quantity && kit.title.match(/^Kits?\s/i)) {
        // Extract first number found in title
        const numberMatch = kit.title.match(/(\d+)/);
        quantity = numberMatch ? parseInt(numberMatch[1], 10) : null;
      }

      // Third priority: if not a kit, assume it's a single product
      if (!quantity && !kit.title.match(/^Kits?\s/i)) {
        quantity = 1;
      }

      if (!quantity || quantity <= 0) {
        continue;
      }

      // Get the price from the first variant (products from metafield have variants array)
      const price = kit.variants && kit.variants[0] ? kit.variants[0].price : kit.price;

      if (!price) {
        continue;
      }

      // Calculate unit price (price might be in cents or reais depending on source)
      // If price is greater than 10000, it's likely in cents
      const priceInReais = price > 10000 ? price / 100 : price;
      const unitPrice = priceInReais / quantity;

      // Format both total price and unit price
      const formattedTotalPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(priceInReais);

      const formattedUnitPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(unitPrice);

      // Create kit item with exact structure from original
      html += `
        <div class="kit-item">
          <a href="/products/${kit.handle}" style="text-decoration: none; color: inherit;">
            <h3>${kit.title}</h3>
            <p>${formattedTotalPrice} - Preço por unidade ${formattedUnitPrice}</p>
          </a>
        </div>
      `;
      renderedCount++;
    }

    html += `
      </div>
    `;

    // Add simple CSS styles for kit items
    if (!document.getElementById('kits-relacionados-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'kits-relacionados-styles';
      styleElement.textContent = `
        .kit-item {
          border: 1px solid #ddd;
          padding: 10px;
          margin-bottom: 10px;
          border-radius: 5px;
        }

        .kit-item h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 400;
        }

        .kit-item p {
          margin: 5px 0 0;
          font-size: 14px;
          color: #333;
        }

        #kits-relacionados > h2 {
          font-size: 16px;
          font-weight: 400;
          margin-bottom: 8px;
        }
      `;
      document.head.appendChild(styleElement);
    }

    // Check if kits element already exists (should not happen as we check earlier, but double-check)
    const existingKits = document.getElementById('kits-relacionados');
    if (existingKits) {
      existingKits.remove();
    }

    // Create a container div and set its HTML
    const container = document.createElement('div');
    container.innerHTML = html;

    // Insert the kits element after the aviso element (same as the other script)
    avisoElement.insertAdjacentElement('afterend', container.firstElementChild);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CompleteLook();
  });
} else {
  new CompleteLook();
}